import {
  type FormConfig,
  isReturnShapeArraySchema,
  type LayerSchema,
  type ProjectSchemaJSON,
  type QueryJSON,
  type ServiceMapJSON,
  schemaMatchesShapeName,
  serializePropertyRef,
  shapeHasArrayPropOfShape
} from '@takeshape/schema';
import type { Maybe } from '@takeshape/util';
import { useActiveSchema } from 'app/branches/hooks/use-active-schema';
import { getPreviewBranchServiceLayers } from 'app/branches/selectors';
import type { ServiceLayerStateMap } from 'app/branches/types';
import { flattenTemplates } from 'app/common/utils/flatten-templates';
import type { ImmutableMap } from 'app/common/utils/immutable-types';
import type { WidgetProps } from 'app/schema-form/types';
import { getWidgetId, useWidgetTitle } from 'app/schema-form/util/widget';
import { type SyntheticEvent, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

function getServiceInfo(services: ServiceMapJSON, serviceId: string): Maybe<{ namespace?: string; provider: string }> {
  if (serviceId === 'local') {
    return { provider: 'takeshape' };
  }

  const serviceConfig = services[serviceId];
  if (serviceConfig) {
    return {
      provider: serviceConfig.provider,
      namespace: serviceConfig.namespace
    };
  }
}

type OmitOptionArgs = {
  workingCopy: ProjectSchemaJSON;
  serviceLayer: LayerSchema;
  query: QueryJSON;
  requireReturnShape?: string;
  requirePaginatedReturnShape?: string;
};

const omitOption = ({
  workingCopy,
  serviceLayer,
  query,
  requireReturnShape,
  requirePaginatedReturnShape
}: OmitOptionArgs): boolean => {
  if (requireReturnShape) {
    return query.shape !== requireReturnShape;
  }

  if (requirePaginatedReturnShape) {
    if (isReturnShapeArraySchema(query.shape)) {
      if (schemaMatchesShapeName(workingCopy, query.shape.items, requirePaginatedReturnShape)) {
        return false;
      }
    } else {
      const queryShape = workingCopy.shapes[query.shape] ?? serviceLayer.shapes[query.shape];
      if (shapeHasArrayPropOfShape(workingCopy, queryShape, requirePaginatedReturnShape)) {
        return false;
      }
    }

    return true;
  }

  return false;
};

export type QueryOption = {
  name: string;
  namespace?: string;
  serviceId: string;
  provider: string;
  ref: string;
};

export function getOptions(
  workingCopy: ProjectSchemaJSON | undefined,
  serviceLayers: ServiceLayerStateMap,
  config: ImmutableMap<FormConfig>
): QueryOption[] {
  if (!workingCopy) return [];
  const requireReturnShape: string | undefined = config.get('requireReturnShape');
  const requirePaginatedReturnShape: string | undefined = config.get('requirePaginatedReturnShape');
  const includeMutations: boolean | undefined = config.get('includeMutations');
  const localOnly: boolean | undefined = config.get('localOnly');
  const services = workingCopy.services ?? {};
  const options: QueryOption[] = [];
  const added = new Set<string>();
  const addLayerOptions = (serviceId: string, serviceLayer: LayerSchema, queries: 'queries' | 'mutations') => {
    for (const name of Object.keys(serviceLayer[queries])) {
      const query = serviceLayer[queries][name];
      const serviceInfo = getServiceInfo(services, serviceId);
      if (serviceInfo) {
        const { namespace, provider } = serviceInfo;
        const namespacedName = namespace ? `${namespace}_${name}` : name;
        if (
          omitOption({
            workingCopy,
            serviceLayer,
            query,
            requireReturnShape,
            requirePaginatedReturnShape
          })
        ) {
          continue;
        }

        if (!added.has(namespacedName)) {
          added.add(namespacedName);
          options.push({
            provider,
            namespace,
            name,
            serviceId,
            ref: serializePropertyRef({
              layerId: serviceId,
              shapeName: queries === 'queries' ? 'Query' : 'Mutation',
              propertyName: name
            })
          });
        }
      }
    }
  };

  if (!localOnly) {
    for (const [serviceId, layer] of Object.entries(serviceLayers)) {
      if (layer.data) {
        addLayerOptions(serviceId, layer.data, 'queries');
        if (includeMutations) {
          addLayerOptions(serviceId, layer.data, 'mutations');
        }
      }
    }
  }

  const flattenedWorkingCopy = flattenTemplates(workingCopy);
  addLayerOptions('local', flattenedWorkingCopy, 'queries');
  if (includeMutations) {
    addLayerOptions('local', flattenedWorkingCopy, 'mutations');
  }

  return options;
}

export type SelectQueryHookProps = Record<string, unknown> & WidgetProps;

export const useSelectQuery = (props: SelectQueryHookProps) => {
  const { schema, config, disabled, readOnly, isRequired, onChange } = props;
  const { activeRuntimeSchemaJSON } = useActiveSchema();
  const serviceLayers = useSelector(getPreviewBranchServiceLayers);
  const requireReturnShape: string | undefined = config.get('requireReturnShape');

  const description = schema.get('description');
  const instructions = config?.get('instructions');
  const label = useWidgetTitle(props);
  const id = getWidgetId(props);

  const handleChange = useCallback(
    (event: SyntheticEvent, value: QueryOption | null) => {
      // convert null to undefined for schema form
      onChange(value ? value.ref : undefined);
    },
    [onChange]
  );

  const options = useMemo(
    () => getOptions(activeRuntimeSchemaJSON ?? undefined, serviceLayers, config),
    [activeRuntimeSchemaJSON, serviceLayers, config]
  );

  // Controlled inputs expect a non-undefined empty value
  const value = useMemo(
    () => (props.value ? (options.find((option) => option.ref === props.value) ?? null) : null),
    [props.value, options]
  );

  return {
    value,
    options,
    requireReturnShape,
    description,
    instructions,
    label,
    id,
    disabled: Boolean(disabled) || Boolean(readOnly),
    isRequired,
    handleChange
  };
};
